import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { AuthenticationService, DocumentsService } from '@app/core/services';
import { IAGServiceLineTableConfig } from '@app/products/hh/models/ag-service-line-table-config.model';
import { TableColumnConfig } from '@app/shared/models';
import { LibPipeModule } from '@mynexus/mynexus-ui-lib';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { Table, TableModule } from 'primeng/table';

/*
  todo:
  as this components template identic to service-lines-selection.component.ts
  rewrite it for service-based approach
*/
@Component({
    selector: 'mnc-service-lines-table',
    templateUrl: './service-lines-table.component.html',
    imports: [
        CommonModule,
        LibPipeModule,
        MultiSelectModule,
        TableModule,
        InputTextModule,
    ]
})
export class ServiceLinesTableComponent implements OnInit {
  @ViewChild('dt', { static: true }) dataTable!: Table;
  
  @Input() lines!: object[];
  @Output() serviceLinesSelected = new EventEmitter<string[]>();

  tableData: IAGServiceLineTableConfig[];
  columnConfig: TableColumnConfig[] = [];
  selection: object;

  private readonly documentsService = inject(DocumentsService);

  constructor(private readonly authenticationService: AuthenticationService) {
  }

  ngOnInit(): void {
    this.tableData = this.initTableData();
    this.columnConfig = this.initTableConfig();
  }

  selectionChange(data: IAGServiceLineTableConfig[]) {
    this.serviceLinesSelected.emit(data.map((x) => x.serviceLineId));
  }

  clearFilters(): void {
    this.dataTable.clear();
    this.columnConfig = this.initTableConfig();
  }

  private initTableData(): IAGServiceLineTableConfig[] {
    return this.lines.map((x) => ({
      serviceLineId: x['authorizationDetailId'],
      serviceLineNumber: x['sequenceNumber'],
      requestReceivedDate: x['dateTimeFaxRecieved'],
      serviceRequestType: x['requestType'] || 'N/A', // todo: sometimes undefined
      urgency: x['urgency'],
      urgencyChange: x['urgencyChange'],
      skillCode: x['skillCode'],
      serviceStatus: x['serviceStatus'],
      requestedVisits: x['requestedAmount'],
      approvedVisits: x['approvedAmount'],
      deniedVisits: x['deniedAmount'],
      effectiveFrom: x['effectiveFromDate'],
      effectiveThrough: x['effectiveToDate'],
      statusReason: x['serviceStatusReason'],
      createdBy: x['createdByName'],
      queue: x['platformQueue'],
    }))?.sort((a, b) => { return b.serviceLineNumber - a.serviceLineNumber });
  }

  private initTableConfig(): TableColumnConfig[] {
    const serviceLineNumberList = this.documentsService
      .getUniqueFilterOptions(this.tableData.map((x) => x.serviceLineNumber));

    const serviceRequestTypeList = this.documentsService
      .getUniqueFilterOptions(this.tableData.map((x) => x.serviceRequestType));

    const serviceStatusList = this.documentsService
      .getUniqueFilterOptions(this.tableData.map((x) => x.serviceStatus));

    let result = [
      {
        header: 'Service Line Number',
        field: 'serviceLineNumber',
        styles: { minWidth: '140px' },
        isSortable: true,
        filterTemplate: 'multiSelectFilterTemplate',
        filterList: serviceLineNumberList,
      },
      {
        header: 'Date/Time Request Received',
        field: 'requestReceivedDate',
        styles: { minWidth: '220px' },
        isSortable: true,
      },
      {
        header: 'Service Request Type',
        field: 'serviceRequestType',
        styles: { minWidth: '220px' },
        isSortable: true,
        filterTemplate: 'multiSelectFilterTemplate',
        filterList: serviceRequestTypeList,
      },
      {
        header: 'Urgency',
        field: 'urgency',
        styles: { minWidth: '130px' },
        isSortable: true,
      },
      {
        header: 'Skill',
        field: 'skillCode',
        styles: { minWidth: '120px' },
        isSortable: true,
      },
      {
        header: 'Service Status',
        field: 'serviceStatus',
        styles: { minWidth: '190px' },
        isSortable: false,
        filterTemplate: 'multiSelectFilterTemplate',
        filterList: serviceStatusList,
      },
      {
        header: 'Requested Visits',
        field: 'requestedVisits',
        styles: { minWidth: '120px' },
        isSortable: true,
      },
      {
        header: 'Approved Visits',
        field: 'approvedVisits',
        styles: { minWidth: '120px' },
        isSortable: true,
      },
      {
        header: 'Denied Visits',
        field: 'deniedVisits',
        styles: { minWidth: '120px' },
        isSortable: true,
      },
      {
        header: 'Effective From',
        field: 'effectiveFrom',
        styles: { minWidth: '180px' },
        isSortable: true,
      },
      {
        header: 'Effective To',
        field: 'effectiveThrough',
        styles: { minWidth: '180px' },
        isSortable: true,
      },
      {
        header: 'Status Reason',
        field: 'statusReason',
        styles: { minWidth: '250px' },
        isSortable: true,
      },
      {
        header: 'Created By',
        field: 'createdBy',
        styles: { minWidth: '210px' },
        isSortable: true,
      },
    ];

    // external users cannot see queue column
    if (!this.authenticationService.isExternalUser) {
      result.push({
        header: 'Queue',
        field: 'queue',
        styles: { minWidth: '180px' },
        isSortable: true,
      });

      const urgencyChangeOrderindex = 4;
      result = [
        ...result.slice(0, urgencyChangeOrderindex),
        {
          header: 'Urgency Change',
          field: 'urgencyChange',
          styles: { minWidth: '120px' },
          isSortable: true,
        },
        ...result.slice(urgencyChangeOrderindex),
      ];
    }

    return result;
  }
}
